import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Factory } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/layout/Logo";
import { FactoryIntro } from "@/pages/factory/FactoryIntro";

const SUBNAV = [
  { to: "overview", label: "Overview" },
  { to: "model", label: "Model" },
  { to: "orders", label: "Orders & Production" },
  { to: "assembly", label: "Factory Assembly" },
];

/** Dark, full-screen factory workspace — an integrated OkyanusHub module. */
export default function FactoryLayout() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  // Play the logo-assembly intro once per entry into the factory workspace.
  const [intro, setIntro] = useState(true);

  // Same session, same project — only the workspace chrome changes.
  useEffect(() => {
    let alive = true;
    api
      .get(`/projects/${projectId}`)
      .then(({ data }) => {
        if (alive) setProject(data?.project || data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  return (
    <div
      data-testid="factory-layout"
      className="flex h-dvh flex-col bg-[#0A0F1A] text-slate-200"
    >
      <header className="flex h-14 shrink-0 items-center gap-2.5 overflow-hidden border-b border-white/10 bg-[#0D1526] px-4 sm:gap-4 sm:px-6">
        <button
          type="button"
          data-testid="factory-back-to-project"
          onClick={() => navigate(`/projects/${projectId}`)}
          className="mono flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-cyan-500/40 px-3.5 text-[10.5px] uppercase tracking-wider text-cyan-300 transition-colors hover:bg-cyan-500/10"
        >
          <ArrowLeft size={13} />
          <span className="hidden sm:inline">Back to Project</span>
        </button>

        <div className="hidden h-6 w-px bg-white/10 sm:block" />

        <div className="flex shrink-0 items-center gap-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-500/15 text-cyan-400">
            <Factory size={16} />
          </span>
          <div className="leading-tight">
            <p className="mono text-[10px] uppercase tracking-[0.22em] text-cyan-400/80">
              Okyanus
            </p>
            <p
              data-testid="factory-neon-title"
              className="font-display text-[15px] font-bold uppercase tracking-[0.3em] text-white"
              style={{
                textShadow:
                  "0 0 4px rgba(255,255,255,.85), 0 0 10px rgba(103,232,249,.8), 0 0 22px rgba(34,211,238,.55), 0 0 44px rgba(34,211,238,.3)",
              }}
            >
              Factory
            </p>
          </div>
        </div>

        <div className="hidden h-6 w-px bg-white/10 sm:block" />

        <div className="min-w-0" data-testid="factory-project-name">
          <p className="mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Project</p>
          <p className="truncate text-[12.5px] font-medium text-slate-200">
            {project
              ? `${project.code ? `${project.code} · ` : ""}${project.name || ""}`
              : "…"}
          </p>
        </div>

        <div className="ml-auto hidden shrink-0 items-center sm:flex">
          <span className="rounded-lg bg-white/95 px-2 py-1 shadow-sm">
            <Logo size={22} />
          </span>
        </div>
      </header>

      <nav
        data-testid="factory-subnav"
        className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-white/10 bg-[#0B1220] px-3 sm:px-5"
      >
        {SUBNAV.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            data-testid={`factory-nav-${tab.to}`}
            className={({ isActive }) =>
              `mono relative shrink-0 px-3 py-3 text-[10.5px] uppercase tracking-[0.16em] transition-colors ${
                isActive ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {tab.label}
                <span
                  className={`absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-cyan-400 transition-opacity ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>

      {intro ? <FactoryIntro onDone={() => setIntro(false)} /> : null}
    </div>
  );
}
